import { Tooltip, TooltipContent, TooltipTrigger } from '@1trading/react';
import { CircleAlert, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { StatusChip } from '@/components/ui/status-chip';
import { MAX_RANGE_DAYS } from '@/features/abuse/abuse-filters';
import { abuseSubjectPath, formatScore, getSubjectChip } from '@/features/abuse/abuse-meta';
import { useAbuseSubject } from '@/hooks/queries/compliance/use-abuse';
import { isNotFoundError } from '@/lib/api/error';
import type { AbuseSubjectDetail, AbuseSubjectKey } from '@/services/api/abuse';
import { formatDate } from '@/utils/format';
import { pluralize } from '@/utils/string';

const describeActivity = ({ subject, events, isEventsCapped, case: abuseCase }: AbuseSubjectDetail): string[] => {
  const eventCount = isEventsCapped ? `${events.length}+ events` : pluralize(events.length, 'event');

  return [
    subject ? `Score ${formatScore(subject.score)}, peak ${formatScore(subject.peakScore)} at ${formatDate(subject.peakAt)}` : 'No live score: it expired after a quiet period',
    `${eventCount} in the last ${MAX_RANGE_DAYS} days`,
    ...(abuseCase ? [`Compliance case ${abuseCase.status}`] : []),
  ];
};

/** Links to the subject's abuse page; renders nothing when the subject has no abuse on record (a 404) */
export const AbuseStatusChip = ({ subjectKey }: { subjectKey: AbuseSubjectKey }) => {
  const query = useAbuseSubject(subjectKey);

  if (query.isPending || isNotFoundError(query.error)) return null;
  if (query.isError) {
    return (
      <StatusChip icon={CircleAlert} size="sm" variant="muted">
        Abuse unavailable
      </StatusChip>
    );
  }

  const chip = getSubjectChip(query.data.subject);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" to={abuseSubjectPath(subjectKey)}>
          <StatusChip icon={ShieldAlert} size="sm" variant={chip.variant}>
            {chip.label}
          </StatusChip>
        </Link>
      </TooltipTrigger>
      <TooltipContent className="max-w-72 text-xs leading-relaxed">
        {describeActivity(query.data).map((line) => (
          <p key={line}>{line}</p>
        ))}
      </TooltipContent>
    </Tooltip>
  );
};
AbuseStatusChip.displayName = 'AbuseStatusChip';
